import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, CircularProgress, Typography, makeStyles } from '@material-ui/core';

// The keep / revert card shown bottom-right of the board after a clean-up. Its tinted fill
// drains downward over the whole duration; when it is gone the card goes too and the change
// is kept. Mounted outside the assistant drawer so it survives the drawer closing.
const useStyles = makeStyles((theme) => ({
    '@keyframes tidyDrain': {
        from: { width: '100%' },
        to: { width: '0%' },
    },
    root: {
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: theme.zIndex.snackbar - 1,
        width: 320,
        maxWidth: 'calc(100vw - 48px)',
        borderRadius: 12,
        backgroundColor: '#fff',
        boxShadow: theme.shadows[8],
        overflow: 'hidden',
    },
    // Anchored left and shrinking in width: the remaining time drains from right to left.
    fill: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '100%',
        background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        opacity: 0.35,
        animationName: '$tidyDrain',
        animationTimingFunction: 'linear',
        animationFillMode: 'forwards',
        pointerEvents: 'none',
    },
    body: {
        position: 'relative',
        padding: theme.spacing(1.5, 2),
    },
    text: {
        fontSize: '0.9rem',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    button: {
        marginLeft: theme.spacing(1),
        whiteSpace: 'nowrap',
        textTransform: 'none',
    },
}));

const TidyRevertPopup = ({ durationMs = 30000, onKeep, onRevert }) => {
    const classes = useStyles();
    const [reverting, setReverting] = useState(false);
    // Read through refs so parent re-renders never restart the countdown.
    const onKeepRef = useRef(onKeep);
    const onRevertRef = useRef(onRevert);
    onKeepRef.current = onKeep;
    onRevertRef.current = onRevert;

    useEffect(() => {
        const timer = setTimeout(() => {
            if (onKeepRef.current) onKeepRef.current();
        }, durationMs);
        return () => clearTimeout(timer);
    }, [durationMs]);

    const handleRevert = async () => {
        if (reverting) return;
        setReverting(true);
        try {
            if (onRevertRef.current) await onRevertRef.current();
        } finally {
            setReverting(false);
        }
    };

    return (
        <div className={classes.root} role='status' aria-live='polite'>
            <div className={classes.fill} style={{ animationDuration: `${durationMs}ms` }} />
            <div className={classes.body}>
                <Typography className={classes.text}>
                    <FormattedMessage id='ai.tidy.done' />
                </Typography>
                <div className={classes.actions}>
                    <Button size='small' className={classes.button} onClick={() => onKeep && onKeep()} disabled={reverting}>
                        <FormattedMessage id='ai.tidy.keep' />
                    </Button>
                    <Button
                        size='small'
                        variant='outlined'
                        color='primary'
                        className={classes.button}
                        onClick={handleRevert}
                        disabled={reverting}
                        endIcon={reverting ? <CircularProgress size={14} /> : null}
                    >
                        <FormattedMessage id={reverting ? 'ai.tidy.reverting' : 'ai.tidy.revert'} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TidyRevertPopup;
